import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Row, Col, Table, Alert, Badge } from 'react-bootstrap';

/**
 * Pannello di debug per analizzare i problemi nei match e nelle associazioni
 * Mostra i dettagli interni dei match, team e referenze per risolvere problemi di persistenza
 */
function DebugPanel() {
  // Stati per i dati
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [stableIdRegistry, setStableIdRegistry] = useState({});
  const [activeTab, setActiveTab] = useState('teams');

  // Carica i dati da localStorage all'avvio
  useEffect(() => {
    loadData();
  }, []);

  // Carica tutti i dati rilevanti da localStorage
  const loadData = () => {
    try {
      // Carica teams
      const teamsData = localStorage.getItem('teams');
      const parsedTeams = teamsData ? JSON.parse(teamsData) : [];
      setTeams(parsedTeams);

      // Carica matches
      const matchesData = localStorage.getItem('matches');
      const parsedMatches = matchesData ? JSON.parse(matchesData) : [];
      setMatches(parsedMatches);

      // Carica registro ID stabili
      const registryData = localStorage.getItem('_stable_id_registry');
      const parsedRegistry = registryData ? JSON.parse(registryData) : {};
      setStableIdRegistry(parsedRegistry);

      console.log('[DEBUG] Dati caricati:', { teams: parsedTeams, matches: parsedMatches, registry: parsedRegistry });
    } catch (error) {
      console.error('Errore nel caricamento dei dati:', error);
    }
  };

  // Funzione per verificare la corrispondenza tra ID stabili e nomi nei match
  const analyzeMatchConsistency = () => {
    if (!matches.length || !teams.length) return [];

    const results = [];
    const teamIdToName = {};

    // Crea una mappa degli ID dei team ai nomi
    teams.forEach(team => {
      if (team.stableId) {
        teamIdToName[team.stableId] = team.members?.[0] || 'unnamed';
      }
    });

    // Analizza ogni match
    matches.forEach(match => {
      // Verifica che gli indici teamA/teamB corrispondano ai nomi tiratorA/tiratorB
      let teamAName = teams[match.teamA]?.members?.[0] || 'non trovato';
      let teamBName = teams[match.teamB]?.members?.[0] || 'non trovato';

      // Controllo di consistenza
      const consistentA = match.tiratorA === teamAName;
      const consistentB = match.tiratorB === teamBName;

      results.push({
        matchId: match.id,
        teamA: match.teamA,
        teamB: match.teamB,
        teamAName,
        teamBName,
        tiratorA: match.tiratorA || 'mancante',
        tiratorB: match.tiratorB || 'mancante',
        stableIdA: match.stableIdA || 'mancante',
        stableIdB: match.stableIdB || 'mancante',
        consistent: consistentA && consistentB,
        completed: match.completed,
        score: match.scoreA !== undefined && match.scoreB !== undefined
          ? `${match.scoreA} - ${match.scoreB}`
          : 'non disputato'
      });
    });

    return results;
  };

  // Analisi delle corrispondenze
  const matchAnalysis = analyzeMatchConsistency();

  // Formatta il JSON per la visualizzazione
  const formatJson = (obj) => {
    return JSON.stringify(obj, null, 2);
  };

  // Riparazione automatica dei match
  const repairMatches = () => {
    if (!matches.length || !teams.length) return;

    // Crea una mappa dei nomi dei tiratori agli indici dei team
    const nameToTeamIndex = {};
    teams.forEach((team, index) => {
      const name = team.members?.[0];
      if (name) {
        nameToTeamIndex[name] = index;
      }
    });

    // Ripara i match usando i nomi dei tiratori
    const repairedMatches = matches.map(match => {
      const newMatch = { ...match };

      // Se il match ha i nomi dei tiratori ma non indici corretti
      if (match.tiratorA && match.tiratorB) {
        // Aggiorna gli indici dei team in base ai nomi dei tiratori
        if (nameToTeamIndex[match.tiratorA] !== undefined) {
          newMatch.teamA = nameToTeamIndex[match.tiratorA];
        }
        if (nameToTeamIndex[match.tiratorB] !== undefined) {
          newMatch.teamB = nameToTeamIndex[match.tiratorB];
        }
      }

      return newMatch;
    });

    // Salva i match riparati
    localStorage.setItem('matches', JSON.stringify(repairedMatches));
    localStorage.setItem('_backup_repaired_matches', JSON.stringify(matches)); // backup originali

    // Ricarica i dati
    setMatches(repairedMatches);
    alert('Riparazione completata! I match sono stati aggiornati con indici corretti.');
  };

  // Elimina tutti i match completati
  const resetCompletedMatches = () => {
    if (!matches.length) return;

    const resetMatches = matches.map(match => ({
      ...match,
      completed: false,
      scoreA: undefined,
      scoreB: undefined,
      winner: undefined,
      notes: undefined
    }));

    // Salva i match resettati
    localStorage.setItem('matches', JSON.stringify(resetMatches));
    localStorage.setItem('_backup_reset_matches', JSON.stringify(matches)); // backup originali

    // Ricarica i dati
    setMatches(resetMatches);
    alert('Reset completato! Tutti i match sono stati reimpostati come non disputati.');
  };

  // Reset completo del registro degli ID stabili
  const resetStableIdRegistry = () => {
    // Elenco di chiavi potenziali da rimuovere
    const keysToRemove = [
      '_stable_id_registry',
      'stableIdRegistry',
      '_stable_id_backup',
      'athlete_registry',
      '_backup_stable_id_registry'
    ];

    // Rimozione di tutte le chiavi potenziali
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`Tentativo di rimozione della chiave: ${key}`);
    });

    // Forza l'aggiornamento dello stato e dell'interfaccia
    setStableIdRegistry({});
    console.log('Registry degli ID stabili completamente cancellato');
    alert('Registry degli ID stabili cancellato con successo!');

    // Crea un nuovo registro vuoto per evitare problemi di riferimento
    localStorage.setItem('_stable_id_registry', JSON.stringify({}));

    // Ricarica i dati per aggiornare la UI
    loadData();
  };

  return (
    <Container className="mt-4">
      <h1>Pannello di Debug</h1>
      <Alert variant="info">
        Questo pannello aiuta a diagnosticare e risolvere i problemi di persistenza dei dati tra i match e i tiratori.
      </Alert>

      <Row className="mb-4">
        <Col>
          <Button variant="primary" onClick={loadData} className="me-2">
            Ricarica Dati
          </Button>
          <Button variant="success" onClick={repairMatches} className="me-2">
            Ripara Match
          </Button>
          <Button variant="danger" onClick={resetCompletedMatches} className="me-2">
            Reset Match Completati
          </Button>
          <Button variant="warning" onClick={resetStableIdRegistry}>
            Reset Registry ID Stabili
          </Button>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Button
            variant={activeTab === 'teams' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveTab('teams')}
            className="me-2"
          >
            Team ({teams.length})
          </Button>
          <Button
            variant={activeTab === 'matches' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveTab('matches')}
            className="me-2"
          >
            Match ({matches.length})
          </Button>
          <Button
            variant={activeTab === 'analysis' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveTab('analysis')}
            className="me-2"
          >
            Analisi ({matchAnalysis.length})
          </Button>
          <Button
            variant={activeTab === 'registry' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveTab('registry')}
          >
            Registry ({Object.keys(stableIdRegistry).length})
          </Button>
        </Col>
      </Row>

      {activeTab === 'teams' && (
        <Card>
          <Card.Header>Teams ({teams.length})</Card.Header>
          <Card.Body>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Indice</th>
                  <th>Nome</th>
                  <th>Stable ID</th>
                  <th>Pool</th>
                </tr>
              </thead>
              <tbody>
                {teams.map((team, idx) => (
                  <tr key={`team-${idx}`}>
                    <td>{idx}</td>
                    <td>{team.members?.[0] || 'unnamed'}</td>
                    <td>
                      <code>{team.stableId || 'non definito'}</code>
                    </td>
                    <td>{team.pool}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {activeTab === 'matches' && (
        <Card>
          <Card.Header>Matches ({matches.length})</Card.Header>
          <Card.Body>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Pool</th>
                  <th>TeamA</th>
                  <th>TeamB</th>
                  <th>TiratorA</th>
                  <th>TiratorB</th>
                  <th>Stato</th>
                </tr>
              </thead>
              <tbody>
                {matches.map((match, idx) => (
                  <tr key={`match-${idx}`} className={match.completed ? 'table-success' : ''}>
                    <td><small><code>{match.id}</code></small></td>
                    <td>{match.pool}</td>
                    <td>{match.teamA}</td>
                    <td>{match.teamB}</td>
                    <td>{match.tiratorA || <Badge bg="warning">mancante</Badge>}</td>
                    <td>{match.tiratorB || <Badge bg="warning">mancante</Badge>}</td>
                    <td>
                      {match.completed ? (
                        <Badge bg="success">{match.scoreA} - {match.scoreB}</Badge>
                      ) : (
                        <Badge bg="secondary">non disputato</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {activeTab === 'analysis' && (
        <Card>
          <Card.Header>Analisi di Consistenza ({matchAnalysis.length})</Card.Header>
          <Card.Body>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Match ID</th>
                  <th>Indici (A/B)</th>
                  <th>Nomi da Team</th>
                  <th>Nomi da Match</th>
                  <th>Consistenza</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {matchAnalysis.map((analysis, idx) => (
                  <tr key={`analysis-${idx}`} className={analysis.consistent ? 'table-success' : 'table-danger'}>
                    <td><small><code>{analysis.matchId}</code></small></td>
                    <td>{analysis.teamA} / {analysis.teamB}</td>
                    <td>{analysis.teamAName} / {analysis.teamBName}</td>
                    <td>{analysis.tiratorA} / {analysis.tiratorB}</td>
                    <td>
                      {analysis.consistent ? (
                        <Badge bg="success">Consistente</Badge>
                      ) : (
                        <Badge bg="danger">Inconsistente</Badge>
                      )}
                    </td>
                    <td>
                      {analysis.completed ? (
                        <Badge bg="success">{analysis.score}</Badge>
                      ) : (
                        <Badge bg="secondary">-</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {activeTab === 'registry' && (
        <Card>
          <Card.Header>Registry degli ID Stabili ({Object.keys(stableIdRegistry).length})</Card.Header>
          <Card.Body>
            <pre className="border p-3 bg-light">
              {formatJson(stableIdRegistry)}
            </pre>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}

export default DebugPanel;
